import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { SingleSelectAutoComplete } from "@/Common/Components/Inputs/SingleSelectAutocomplete";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ISendRecallPopupModel } from "@/Communications/Models/UI/IRecallPopupModel";

interface ISendRecallPopupControl {
    ContainerId: string;
    Model: ISendRecallPopupModel;
}

class SendRecallPopupControl extends BaseReactComponent<ISendRecallPopupControl> {
    private clinicianSearchStrategy: ClinicianSearchStrategy;

    constructor(props: ISendRecallPopupControl) {
        super(props);

        this.clinicianSearchStrategy = new ClinicianSearchStrategy(cacheStore.CliniciansAndEmployeeGroups);
    }

    public componentDidMount(): void {
        super.setOverlay("SendRecallPopupControl");

        let elem = $("#" + this.props.ContainerId);
        let cnt = document.body;
        let dragOptions = {
            containment: cnt,
            handle: ".p-header",
            start: function() {
                elem.css("transform", "none");
            },
            stop: function() {
                elem.draggable("destroy");
                window.setTimeout(
                    () => {
                        elem.draggable(dragOptions);
                        elem.css("transform", "none");
                    },
                    80
                );
            }
        };
        elem.draggable(dragOptions);
        $(window).trigger("resize");

        var elemCreateNote = document.getElementById("recall_create_note");
        autosize(elemCreateNote);

        globalSendCommunicationStore.ResetRecallModel();

        var operation = new LocalEvent(LocalOperations.SendRecallPopup.Mounted);
        LocalEventDispatcher.dispatch(operation);
    }

    public render(): JSX.Element {
        let isDisabled = false;

        var hasPatients = this.props.Model.get("SelectedPatients").size > 0;
        var hasClinician = this.props.Model.get("SelectedClinicians").size > 0;
        var hasDescription = this.props.Model.get("DescriptionText").trim().length > 0;
        isDisabled = !hasPatients || !hasClinician || !hasDescription;

        return (
            <div id={this.props.ContainerId} className="popup task" style={{ minHeight: "auto", width: "902px" }} role="dialog">
                <div className="row p-header">
                    <div className="col-lg-12">
                        <a className="close" aria-label="Close" onClick={() => this.closeWindow()}>×</a>
                        <h3>{cacheStore.GetString("NewRecallLabel").toUpperCase()}</h3>
                    </div>
                </div>
                <div className="p-body myle-form">
                    <div className="row">
                        <div className="col-lg-2 col-md-3 col-sm-12 text-right">
                            <label className="form-label">{cacheStore.GetString("PatientsLabel")}</label>
                        </div>
                        <div className="col-lg-9 col-md-9 col-sm-12">
                            <PersonSearchAutocompleteCustom
                                valid={this.props.Model.get("SelectedPatients").size > 0}
                                useBlurEvent={true}
                                componentId="recall_create_addpatient"
                                selectedItemList={this.props.Model.get("SelectedPatients")}
                                selectionOp={LocalOperations.SendRecallPopup.PatientSelect}
                                clearOp={LocalOperations.SendRecallPopup.PatientSelect}
                                focusOnLoad={false}
                                closeDropDownAfterSelection={true}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-2 col-md-3 col-sm-12 text-right">
                            <label className="form-label">{cacheStore.GetString("DescriptionLabel")}</label>
                        </div>
                        <div className="col-lg-9 col-md-9 col-sm-12">
                            <SingleSelectAutoComplete
                                id={this.props.Model.get("RecallPopupId")}
                                valid={hasDescription}
                                useblurevent
                                componentId="recall_create_description"
                                selectedItems={this.props.Model.get("SelectedDescriptions")}
                                displayedTextValue={this.props.Model.get("DescriptionText")}
                                selectionOp={LocalOperations.SendRecallPopup.DescriptionSelect}
                                clearOp={LocalOperations.SendRecallPopup.DescriptionClear}
                                focusOnLoad
                                changedTextValueEvent={LocalOperations.SendRecallPopup.DescriptionChange}
                                closeDropDownAfterSelection
                                dataCy="sendRecallPopup-description"
                            />

                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-2 col-md-3 col-sm-12 text-right">
                            <label className="form-label">{cacheStore.GetString("ResponsibleProvidersLabel")}</label>
                        </div>
                        <div className="col-lg-9 col-md-9 col-sm-12">
                            <PersonSearchAutocompleteCustom
                                valid={this.props.Model.get("SelectedClinicians").size > 0}
                                componentId="recall_clinician"
                                searchStrategy={this.clinicianSearchStrategy}
                                selectedItemList={this.props.Model.get("SelectedClinicians")}
                                selectionOp={LocalOperations.SendRecallPopup.ClinicianSelect}
                                clearOp={LocalOperations.SendRecallPopup.ClinicianSelect}
                                focusOnLoad={false}
                                disabled={false}
                                closeDropDownAfterSelection={true}
                                allowMultipleItemSelection={true}
                            />
                        </div>
                    </div>

                    <div className="row qr">
                        <div className="col-lg-2 col-md-3 col-sm-12 text-right">
                            <label className="form-label">{cacheStore.GetString("SpecialtyLabel")}</label>
                        </div>
                        <div className="col-lg-9 col-md-9 col-sm-12">
                            <select onChange={e => this.OnSpecialityChange(e)} className="form-control myle-control" value={this.props.Model.get("SelectedSpecializationId")} data-cy="sendRecallPopup-speciality">
                                {this.props.Model.get("Specializations").toArray().map(spec => (
                                    <option key={spec.LookupID} value={spec.LookupID}> {cacheStore.GetLocalizedValueByLookupId(spec.LookupID)}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="row qr">
                        <div className="col-lg-2 col-md-3 col-sm-12 text-right">
                            <label className="form-label">{cacheStore.GetString("DueDateLabel")}</label>
                        </div>

                        <div className="col-lg-9 col-md-9 col-sm-12">
                            <DateSelector
                                id="task_start_datey"
                                dataCy="sendRecallPopup-dueDate"
                                cssClass=""
                                date={this.props.Model.get("DueDate")}
                                onSelectOp={LocalOperations.SendRecallPopup.DueDateSelect}
                                dateFormat="yyyy/MM/dd"
                                disabled={false}
                                toDayEnabled={true}
                                todayOperation={LocalOperations.SendRecallPopup.DueDateToday}
                            />
                        </div>
                    </div>

                    <div className="row qr">
                        <div className="col-lg-2 col-md-3 col-sm-12 text-right">
                            <label className="form-label">{cacheStore.GetString("PriorityLabel")}</label>
                        </div>
                        <div className="col-lg-9 col-md-9 col-sm-12">
                            <select onChange={e => this.OnPriorityChange(e)} className="form-control myle-control" value={this.props.Model.get("SelectedPriorityId")} data-cy="sendRecallPopup-priority">
                                {this.props.Model.get("Priorities").toArray().map(p => (
                                    <option key={p.LookupID} value={p.LookupID}> {cacheStore.GetLocalizedValueByLookupId(p.LookupID)}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="row qr">
                        <div className="col-lg-2 col-md-3 col-sm-12 text-right">
                            <label className="form-label">{cacheStore.GetString("NotesLabel")}</label>
                        </div>
                        <div className="col-lg-9 col-md-9 col-sm-12">
                            <textarea
                                id="recall_create_note"
                                onChange={e => this.OnNotesChange(e.target.value)}
                                value={this.props.Model.get("Notes")}
                                style={{ maxHeight: "100px" }}
                                className="vresize form-control"
                                data-cy="sendRecallPopup-note"
                            />
                        </div>
                    </div>
                </div>
                <div className="p-footer">
                    <div className="row">
                        <div className="col-lg-12 text-right">
                            <ButtonControl
                                className="btn btn-default push-right-5"
                                label={cacheStore.GetString("CancelLabel")}
                                onClick={() => sessionStore.dispatch(LocalOperations.SendRecallPopup.ClosePopup)}
                            />
                            <ButtonControl
                                className="btn btn-default push-right-5"
                                label={cacheStore.GetString("ClearLabel")}
                                onClick={() => sessionStore.dispatch(LocalOperations.SendRecallPopup.ClearClick)}
                            />
                            <ButtonControl
                                className="btn btn-primary"
                                label={cacheStore.GetString("CreateLabel")}
                                disabled={isDisabled}
                                onClick={() => sessionStore.dispatch(LocalOperations.SendRecallPopup.CreateClick)}
                                dataCy="sendRecallPopup-create"
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private OnSpecialityChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        var operation = new LocalEvent(LocalOperations.SendRecallPopup.SpecialityChange);
        operation.AddItem("value", e.target.value);
        LocalEventDispatcher.dispatch(operation);
    }

    private OnPriorityChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        var operation = new LocalEvent(LocalOperations.SendRecallPopup.PriorityChange);
        operation.AddItem("value", e.target.value);
        LocalEventDispatcher.dispatch(operation);
    }

    private OnNotesChange(value: string): void {
        var operation = new LocalEvent(LocalOperations.SendRecallPopup.NoteChange);
        operation.AddItem("value", value);
        LocalEventDispatcher.dispatch(operation);
    }

    private closeWindow(): void {
        let operation = new LocalEvent(LocalOperations.SendRecallPopup.ClosePopup);
        LocalEventDispatcher.dispatch(operation);
    }
}

export { SendRecallPopupControl };
